import * as THREE from 'three'
import { Line2 } from 'three/addons/lines/Line2.js'
import { LineMaterial } from 'three/addons/lines/LineMaterial.js'
import { LineGeometry } from 'three/addons/lines/LineGeometry.js'
import { useRouteStore } from '@/stores/routeStore.ts'
import { useMapSettingsStore } from '@/stores/mapSettingsStore.ts'
import { pointToLineDistance } from '@/utils/geometry.ts'
import type Route from '@/types/Route.ts'

interface RouteLine {
  routeId: number
  line: Line2
}

export class MapRoutes {
  scene: THREE.Scene
  routeStore: any
  mapSettingsStore: any

  // All route lines (including loop lines)
  routeLines: RouteLine[] = []

  // Waypoint markers for routes
  waypointMarkers: THREE.Mesh[] = []

  // Colors
  routeColor = 0x374151 // Dark gray color for saved routes
  waypointColor = 0x374151 // Dark gray

  // Line width
  lineWidth = 4

  // Z position (lower than route editor to be behind)
  zPosition = 0.05

  constructor(scene: THREE.Scene) {
    this.scene = scene
    this.routeStore = useRouteStore()
    this.mapSettingsStore = useMapSettingsStore()
  }

  /**
   * Update all route visualizations
   */
  update() {
    this.clearAll()

    if (!this.mapSettingsStore.showRoutes) {
      return
    }

    // Draw all routes from the store
    for (const route of this.routeStore.routes.values()) {
      this.drawRoute(route)
    }
  }

  /**
   * Draw a single route
   */
  drawRoute(route: Route) {
    const waypoints = route.waypoints
    if (waypoints.length < 2) return

    // Draw lines connecting waypoints
    for (let i = 0; i < waypoints.length; i++) {
      const curr = waypoints[i]
      const next = waypoints[(i + 1) % waypoints.length] // Loop back to first

      this.addRouteLine(route.id, curr.x, curr.y, next.x, next.y)
    }

    // Draw small waypoint markers
    for (const wp of waypoints) {
      this.addWaypointMarker(wp.x, wp.y)
    }
  }

  /**
   * Add a route line segment
   */
  addRouteLine(routeId: number, x1: number, y1: number, x2: number, y2: number) {
    const geometry = new LineGeometry()
    geometry.setPositions([x1, y1, this.zPosition, x2, y2, this.zPosition])

    const material = new LineMaterial({
      color: this.routeColor,
      linewidth: this.lineWidth,
      transparent: true,
      opacity: 0.5,
      resolution: new THREE.Vector2(window.innerWidth, window.innerHeight)
    })

    const line = new Line2(geometry, material)
    line.name = 'routeLine'
    line.userData = { routeId } // Store route ID for hover detection
    line.computeLineDistances()

    this.scene.add(line)
    this.routeLines.push({ routeId, line })
  }

  /**
   * Add a small waypoint marker
   */
  addWaypointMarker(x: number, y: number) {
    const geometry = new THREE.CircleGeometry(0.5, 12)
    const material = new THREE.MeshBasicMaterial({
      color: this.waypointColor,
      transparent: true,
      opacity: 0.5
    })
    const marker = new THREE.Mesh(geometry, material)
    marker.position.set(x, y, this.zPosition + 0.01)
    marker.name = 'routeWaypointMarker'
    this.scene.add(marker)
    this.waypointMarkers.push(marker)
  }

  /**
   * Find which route is near a given position
   * Returns the route or null if none found
   */
  findRouteNear(x: number, y: number, threshold: number = 3): Route | null {
    for (const route of this.routeStore.routes.values()) {
      const waypoints = route.waypoints
      if (waypoints.length < 2) continue

      // Check each line segment (including loop back)
      for (let i = 0; i < waypoints.length; i++) {
        const p1 = waypoints[i]
        const p2 = waypoints[(i + 1) % waypoints.length]
        const distance = pointToLineDistance(x, y, p1.x, p1.y, p2.x, p2.y)
        if (distance <= threshold) {
          return route
        }
      }
    }
    return null
  }

  /**
   * Set visibility of all route lines
   */
  setVisible(visible: boolean) {
    for (const { line } of this.routeLines) {
      line.visible = visible
    }
    for (const marker of this.waypointMarkers) {
      marker.visible = visible
    }
  }

  /**
   * Clear all route visualizations
   */
  clearAll() {
    // Remove route lines
    for (const { line } of this.routeLines) {
      this.scene.remove(line)
      line.geometry.dispose()
      if (line.material instanceof THREE.Material) {
        line.material.dispose()
      }
    }
    this.routeLines = []

    // Remove waypoint markers
    for (const marker of this.waypointMarkers) {
      this.scene.remove(marker)
      marker.geometry.dispose()
      if (marker.material instanceof THREE.Material) {
        marker.material.dispose()
      }
    }
    this.waypointMarkers = []
  }

  /**
   * Dispose of all resources
   */
  dispose() {
    this.clearAll()
  }
}
